/**
 * The Session (CONTEXT.md, "Session"): the mutable play state layered on a Character. Everything
 * here is plain data saved with the Character; every change to it is a Play Action
 * (./actions.ts) with an inverse. Nothing here reads the Snapshot: the maxima a Session is
 * measured against come from the derived sheet (./maxima.ts) each time they are needed.
 */
import type { ToggleState } from "../character/model";
import type { DeleteAction } from "../engine/rule-elements/grant-item";

export const SESSION_SCHEMA = 1 as const;

/** Foundry's duration units, plus `until-rest` for Effects that end at daily preparations. */
export type DurationUnit = "rounds" | "minutes" | "hours" | "days" | "encounter" | "unlimited" | "until-rest";
export const DURATION_UNITS: readonly DurationUnit[] = ["rounds", "minutes", "hours", "days", "encounter", "unlimited", "until-rest"];
/** Seconds per unit for timed durations (Foundry: DURATION_UNITS). */
export const UNIT_SECONDS: Readonly<Record<DurationUnit, number | null>> = { rounds: 6, minutes: 60, hours: 3600, days: 86400, encounter: null, unlimited: null, "until-rest": null };
export const ROUND_SECONDS = 6;
export const REST_SECONDS = 8 * 3600;

export type ExpiryType = "turn-start" | "turn-end" | null;

export interface EffectDuration {
  unit: DurationUnit;
  /** Count of units; ignored for the untimed units. */
  value: number;
  /** For rounds: whether the Effect ends at the start or the end of the Character's turn in its last round. */
  expiry: ExpiryType;
  sustained: boolean;
}

/** How a granted Session item behaves when its granter leaves and whether it may leave on its own (Foundry: onDeleteActions). */
export interface GrantLink {
  /** The Session id of the Condition or Effect that granted this one. */
  grantedBy: string;
  onDelete: { granter: DeleteAction; grantee: DeleteAction };
}

export interface PersistentDamage {
  formula: string;
  damageType: string;
  /** The flat check DC to end it; 15 unless changed. */
  dc: number;
}

/** A Condition on the Character: an Entry of the conditions Pack, with its value when valued. */
export interface SessionCondition {
  /** Local id, unique within the Session. */
  id: string;
  uuid: string;
  /** The Entry's slug ("frightened"), copied so the Session's own rules need no Entry. */
  slug: string;
  name: string;
  /** The badge value of a valued Condition; null for the rest. */
  value: number | null;
  /** Set when another Session item granted this one; a Condition the user applied has null. */
  grant: GrantLink | null;
  /** Persistent damage Conditions carry their formula and type; every other Condition has null. */
  persistent: PersistentDamage | null;
}

/** An Effect on the Character: an Entry of one of the effect Packs with its start time and duration. */
export interface SessionEffect {
  id: string;
  uuid: string;
  slug: string;
  name: string;
  /** The Effect's level (a heightened spell's rank), when set; the Entry's own otherwise. */
  level: number | null;
  /** The counter badge value, when the Effect has one. */
  badge: number | null;
  /** Session clock (seconds) when the Effect began. */
  start: number;
  duration: EffectDuration;
  grant: GrantLink | null;
}

/** Slot and use expenditure on one Spellcasting Entry (#10 supplies the Entries; the shape is fixed here). */
export interface SpellcastingUse {
  /** Spontaneous and innate-with-slots: expended slot count by rank ("1" to "10", "0" for cantrips). */
  slots: Record<string, number>;
  /** Prepared: per rank, whether each prepared slot (by index) has been cast. */
  prepared: Record<string, boolean[]>;
  /** Innate: expended uses by spell UUID. */
  innate: Record<string, number>;
  /** Staff Entries (#21): charges expended since daily preparations; absent means none. */
  charges?: number;
}

/** A stack of consumables or ammunition as counted in play: the inventory's copy (#11) with a play-time quantity. */
export interface ConsumableStack {
  id: string;
  uuid: string;
  name: string;
  quantity: number;
  /** Uses on the current item of the stack (a wand's daily charge, a multi-use item); null when a use consumes one item. */
  uses: { value: number; max: number; renew: "day" | null } | null;
  /** Whether the stack goes when quantity reaches 0 (Foundry: `uses.autoDestroy`). */
  autoDestroy: boolean;
  /** Ammunition stacks are drawn on by strikes (Foundry item type `ammo`). */
  ammunition: boolean;
}

export interface Session {
  schema: typeof SESSION_SCHEMA;
  /** ISO timestamp of the last start-fresh. */
  startedAt: string;
  /** Seconds elapsed since start-fresh (Foundry: worldTime relative to the Session). */
  clock: number;
  /** The current round of an encounter, or null outside one. */
  round: number | null;
  hp: { value: number; temp: number };
  conditions: SessionCondition[];
  effects: SessionEffect[];
  heroPoints: number;
  focus: number;
  /** By Spellcasting Entry id. */
  spellcasting: Record<string, SpellcastingUse>;
  /** Special resource values by slug key (camel case as the engine keys them). */
  resources: Record<string, number>;
  /** Uses spent of Entries with a frequency (feats and actions), by Entry UUID. */
  uses: Record<string, number>;
  consumables: ConsumableStack[];
  /** Toggle states for this Session, over the Character's own. */
  toggles: Record<string, ToggleState>;
  /** The next local id number. */
  counter: number;
}

/** Death-track caps as Foundry sets them (dying max may be raised by an Entry such as Diehard). */
export const DEATH_CAPS = { dying: 4, wounded: 3, doomed: 4 } as const;
export const HERO_POINTS_MAX = 3;

/** A new local id. The counter is part of the Session so an allocation is reversible. */
export function allocateId(session: Session, prefix: string): { id: string; session: Session } {
  return { id: `${prefix}${session.counter}`, session: { ...session, counter: session.counter + 1 } };
}

export function findCondition(session: Session, id: string): SessionCondition | undefined {
  return session.conditions.find((c) => c.id === id);
}

export function findEffect(session: Session, id: string): SessionEffect | undefined {
  return session.effects.find((e) => e.id === id);
}

/** The Condition key Foundry uses: persistent damage splits by damage type, everything else is the slug. */
export function conditionKey(condition: Pick<SessionCondition, "slug" | "persistent">): string {
  return condition.persistent ? `persistent-damage-${condition.persistent.damageType}` : condition.slug;
}

/** The highest-valued Condition of a slug, as Foundry's `getCondition` returns the active one. */
export function conditionBySlug(session: Session, slug: string): SessionCondition | undefined {
  return [...session.conditions].filter((c) => c.slug === slug).sort((a, b) => (b.value ?? 0) - (a.value ?? 0))[0];
}

export function conditionValue(session: Session, slug: string): number {
  return conditionBySlug(session, slug)?.value ?? 0;
}

/** Whether a granted item is held in place by its granter (Foundry: `isLocked`). */
export function isLocked(session: Session, item: SessionCondition | SessionEffect): boolean {
  if (!item.grant) return false;
  const granterPresent = session.conditions.some((c) => c.id === item.grant!.grantedBy) || session.effects.some((e) => e.id === item.grant!.grantedBy);
  return granterPresent && item.grant.onDelete.grantee === "restrict";
}

/** Total seconds an Effect lasts; Infinity for the untimed units. */
export function totalDuration(duration: EffectDuration): number {
  const per = UNIT_SECONDS[duration.unit];
  return per === null ? Infinity : Math.max(0, duration.value) * per;
}

/**
 * Whether an Effect has run out at the Session clock (Foundry: calculateRemainingDuration). A
 * turn-end expiry lasts through the Character's turn of its final round, so it ends only once
 * the clock has passed the boundary; everything else ends on reaching it.
 */
export function remainingDuration(effect: SessionEffect, clock: number): { remaining: number; expired: boolean } {
  const total = totalDuration(effect.duration);
  if (!Number.isFinite(total)) return { remaining: Infinity, expired: false };
  const remaining = effect.start + total - clock;
  const expired = effect.duration.expiry === "turn-end" ? remaining < 0 : remaining <= 0;
  return { remaining, expired };
}

/** "3 rounds", "1 minute", "until the encounter ends" ... */
export function describeDuration(duration: EffectDuration): string {
  switch (duration.unit) {
    case "encounter": return "until the encounter ends";
    case "unlimited": return "unlimited";
    case "until-rest": return "until daily preparations";
    default: {
      const n = Math.max(0, duration.value);
      const unit = n === 1 ? duration.unit.slice(0, -1) : duration.unit;
      return `${n} ${unit}${duration.sustained ? " (sustained)" : ""}`;
    }
  }
}

/** "2 rounds left", "8 minutes left", "expired". */
export function describeRemaining(effect: SessionEffect, clock: number): string {
  const { remaining, expired } = remainingDuration(effect, clock);
  if (!Number.isFinite(remaining)) return describeDuration(effect.duration);
  if (expired) return "expired";
  if (remaining >= 86400) return `${Math.ceil(remaining / 86400)} days left`;
  if (remaining >= 3600) return `${Math.ceil(remaining / 3600)} hours left`;
  if (remaining >= 60) return `${Math.ceil(remaining / 60)} minutes left`;
  const rounds = Math.max(0, Math.ceil(remaining / ROUND_SECONDS));
  return remaining === 0 && effect.duration.expiry === "turn-end" ? "until the end of this turn" : `${rounds} round${rounds === 1 ? "" : "s"} left`;
}
